import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Stack, Card, CardContent, CardActions,
  Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Grid, Tabs, Tab, Divider, Alert, CircularProgress,
  Collapse, List, ListItemButton, ListItemText, FormControlLabel, Switch, InputAdornment, Tooltip,
} from '@mui/material';
import {
  Add, Edit, Delete, ExpandMore, ExpandLess, FitnessCenter,
  Lock, LockOpen, Search, Close, DragIndicator, AssignmentTurnedIn, Refresh,
} from '@mui/icons-material';

import { workoutPlansAPI } from '../services/api';
import { useAppData } from '../context/AppDataContext';import AssignPlanDialog from '../components/AssignPlanDialog';
import PaginationBar from '../components/PaginationBar';
import PageHeader from '../components/PageHeader';

const DAY_NAMES = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
// eslint-disable-next-line no-unused-vars
const uid = () => Date.now().toString(36) + Math.random().toString(36).slice(2);

const emptyPlan = () => ({
  name: '', description: '', category: 'general_fitness', difficulty: 'beginner',
  durationWeeks: 4, daysPerWeek: 3, isFree: true,
  restBetweenSets: 30, restBetweenExercises: 30, circuitMode: false,
  isPublished: false, tags: [],
  days: Array.from({ length: 3 }, (_, i) => ({
    dayNumber: i + 1, dayName: DAY_NAMES[i], isRestDay: false, exercises: [],
  })),
});

export default function WorkoutPlans() {
  const { loadExercises } = useAppData();

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [assignPlan, setAssignPlan] = useState(null);
  const [page, setPage]   = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(20);

  // Trigger lazy-load of exercises when this page mounts (no-op if already cached)
  useEffect(() => { loadExercises(); }, [loadExercises]);

  const fetchPlans = useCallback(async () => {
    try {
      setLoading(true);
      const res = await workoutPlansAPI.getAll({ page, limit });
      setPlans(res.data.data || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load plans');
    } finally { setLoading(false); }
  }, [page, limit]);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  const handleSave = async (plan) => {
    try {
      if (plan._id) {
        await workoutPlansAPI.update(plan._id, plan);
        setSuccess('Plan updated');
      } else {
        await workoutPlansAPI.create(plan);
        setSuccess('Plan created');
      }
      setBuilderOpen(false);
      fetchPlans();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save plan');
    }
  };

  const handleDelete = async () => {
    try {
      await workoutPlansAPI.delete(deleteId);
      setSuccess('Plan deleted');
      setDeleteId(null);
      fetchPlans();
    } catch (e) { setError('Failed to delete'); }
  };

  const togglePublish = async (plan) => {
    try {
      await workoutPlansAPI.publish(plan._id, !plan.isPublished);
      fetchPlans();
    } catch (e) { setError('Failed to update'); }
  };

  return (
    <Box p={3}>
      <PageHeader
        title="Workout Plans"
        subtitle="Create and manage workout plans for users."
        gradient="linear-gradient(90deg, #7C3AED, #A78BFA)"
        actions={<>
          <Button variant="outlined" size="small" startIcon={<Refresh />} onClick={fetchPlans}>Refresh</Button>
          <Button variant="contained" size="small" startIcon={<Add />} onClick={() => { setEditingPlan(emptyPlan()); setBuilderOpen(true); }}>Create Plan</Button>
        </>}
      />


      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : plans.length === 0 ? (
        <Box textAlign="center" py={8}>
          <FitnessCenter sx={{ fontSize: 48, color: 'text.disabled', mb: 1.5 }} />
          <Typography variant="body1" fontWeight={600} gutterBottom>No workout plans yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Create your first plan to get started.</Typography>
          <Button variant="contained" startIcon={<Add />} sx={{ mt: 1 }} onClick={() => { setEditingPlan(emptyPlan()); setBuilderOpen(true); }}>
            Create First Plan
          </Button>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {plans.map(plan => (
            <Grid item xs={12} md={6} lg={4} key={plan._id}>
              <Card elevation={0} sx={{ height: '100%', display: 'flex', flexDirection: 'column', border: 1, borderColor: 'divider', borderRadius: 2, transition: 'border-color 0.15s', '&:hover': { borderColor: 'primary.main' } }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
                    <Typography variant="body1" fontWeight={600} sx={{ fontSize: '0.9375rem' }}>{plan.name}</Typography>
                    <Chip label={plan.isPublished ? 'Published' : 'Draft'} color={plan.isPublished ? 'success' : 'default'} size="small" />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" mb={1.5} sx={{ minHeight: 40, fontSize: '0.8125rem' }}>
                    {plan.description || 'No description'}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.5}>
                    <Chip label={plan.difficulty} size="small" variant="outlined" />
                    <Chip label={plan.category?.replace('_', ' ')} size="small" variant="outlined" />
                    <Chip label={`${plan.durationWeeks}w`} size="small" variant="outlined" />
                    <Chip label={plan.isFree ? 'Free' : 'Premium'} size="small" color={plan.isFree ? 'success' : 'warning'} icon={plan.isFree ? <LockOpen sx={{ fontSize: '14px !important' }} /> : <Lock sx={{ fontSize: '14px !important' }} />} />
                  </Stack>
                  <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                    {plan.enrolledCount || 0} enrolled · by {plan.creatorName}
                  </Typography>
                </CardContent>
                <CardActions sx={{ px: 2, pb: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Button size="small" startIcon={<Edit />} onClick={async () => {
                    try {
                      const res = await workoutPlansAPI.getOne(plan._id);
                      const full = res.data.data;
                      const normalized = {
                        ...emptyPlan(),
                        ...full,
                        days: (full.days || []).map(d => ({
                          ...d,
                          exercises: d.exercises || [],
                        })),
                        tags: full.tags || [],
                      };
                      setEditingPlan(normalized);
                      setBuilderOpen(true);
                    } catch {
                      setError('Failed to load plan details');
                    }
                  }}>Edit</Button>
                  <Button size="small" startIcon={plan.isPublished ? <LockOpen /> : <Lock />} onClick={() => togglePublish(plan)}>
                    {plan.isPublished ? 'Unpublish' : 'Publish'}
                  </Button>
                  <Button
                    size="small"
                    startIcon={<AssignmentTurnedIn />}
                    onClick={() => setAssignPlan(plan)}
                    sx={{ color: '#1a1a2ed4' }}
                  >
                    Assign
                  </Button>
                  <IconButton size="small" color="error" sx={{ ml: 'auto' }} onClick={() => setDeleteId(plan._id)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <PaginationBar page={page} pages={pages} total={total} limit={limit} onPage={setPage} onLimit={v => { setLimit(v); setPage(1); }} />

      {/* Plan Builder Dialog */}
      {builderOpen && editingPlan && (
        <PlanBuilderDialog
          plan={editingPlan}
          onSave={handleSave}
          onClose={() => setBuilderOpen(false)}
        />
      )}

      {/* Delete Confirm */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
        <DialogTitle>Delete Plan?</DialogTitle>
        <DialogContent><Typography>This will permanently delete the plan and all its data.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Assign Plan Dialog */}
      <AssignPlanDialog
        open={!!assignPlan}
        onClose={() => { setAssignPlan(null); setSuccess('Plan assigned successfully'); }}
        planType="workout"
        preselectedPlan={assignPlan}
      />
    </Box>
  );
}

// ── Plan Builder Dialog ────────────────────────────────────────────────────────
function PlanBuilderDialog({ plan: initialPlan, onSave, onClose }) {
  const { exercises: cachedExercises } = useAppData();
  const [plan, setPlan] = useState(() => ({
    ...emptyPlan(),
    ...initialPlan,
    days: (initialPlan.days || []).map(d => ({
      ...d,
      exercises: d.exercises || [],
    })),
    tags: initialPlan.tags || [],
  }));
  const [tab, setTab] = useState(0);
  const [exSearch, setExSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Use cached exercises from context — no per-dialog fetch
  const exercises = cachedExercises;

  const update = (field, value) => setPlan(p => ({ ...p, [field]: value }));

  const updateDay = (idx, field, value) => {
    const days = [...plan.days];
    days[idx] = { ...days[idx], [field]: value };
    setPlan(p => ({ ...p, days }));
  };

  const addDay = () => {
    const n = plan.days.length;
    setPlan(p => ({ ...p, days: [...p.days, { dayNumber: n + 1, dayName: DAY_NAMES[n] || `Day ${n + 1}`, isRestDay: false, exercises: [] }] }));
  };

  const removeDay = (idx) => {
    const days = plan.days.filter((_, i) => i !== idx).map((d, i) => ({ ...d, dayNumber: i + 1 }));
    setPlan(p => ({ ...p, days }));
  };

  const addExercise = (dayIdx, ex) => {
    const days = [...plan.days];
    const existing = days[dayIdx].exercises;
    days[dayIdx] = {
      ...days[dayIdx],
      exercises: [...existing, {
        exerciseName: ex.name, muscleGroup: ex.muscleGroup || '',
        sets: 3, reps: 10, weight: null, orderIndex: existing.length,
        isTimed: false, durationSeconds: null,
        notes: ex.instructions || ex.description || '',
        activityType: ex.activityType || 'gym',
        secondsPerRep: ex.secondsPerRep || 2,
      }],
    };
    setPlan(p => ({ ...p, days }));
  };

  const updateExercise = (dayIdx, exIdx, field, value) => {
    const days = [...plan.days];
    days[dayIdx].exercises[exIdx] = { ...days[dayIdx].exercises[exIdx], [field]: value };
    setPlan(p => ({ ...p, days }));
  };

  const removeExercise = (dayIdx, exIdx) => {
    const days = [...plan.days];
    days[dayIdx].exercises = days[dayIdx].exercises.filter((_, i) => i !== exIdx);
    setPlan(p => ({ ...p, days }));
  };

  const handleSave = async () => {
    if (!plan.name.trim()) { setError('Plan name is required'); return; }
    setSaving(true);
    try { await onSave(plan); }
    catch (e) { setError(e.message || 'Save failed'); setSaving(false); }
  };

  const filteredEx = exercises.filter(e =>
    !exSearch || e.name.toLowerCase().includes(exSearch.toLowerCase()) ||
    (e.muscleGroup || '').toLowerCase().includes(exSearch.toLowerCase())
  );

  return (
    <Dialog
      open
      fullWidth
      maxWidth="lg"
      onClose={onClose}
      PaperProps={{
        sx: {
          '& .MuiSvgIcon-root': {
            width: '1em',
            height: '1em',
            flexShrink: 0,
          },
          '& .MuiButton-startIcon > .MuiSvgIcon-root, & .MuiButton-endIcon > .MuiSvgIcon-root': {
            fontSize: '18px',
          },
          '& .MuiIconButton-root .MuiSvgIcon-root': {
            fontSize: '20px',
          },
        },
      }}
    >
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{plan._id ? 'Edit' : 'Create'} Workout Plan</Typography>
          <IconButton onClick={onClose}><Close /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Basic Info" />
          <Tab label={`Days & Exercises (${plan.days.length})`} />
          <Tab label="Settings" />
        </Tabs>

        {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}

        {/* Tab 0: Basic Info */}
        {tab === 0 && (
          <Box p={3}>
            <Grid container spacing={2}>
              <Grid item xs={12}><TextField label="Plan Name *" value={plan.name} onChange={e => update('name', e.target.value)} fullWidth /></Grid>
              <Grid item xs={12}><TextField label="Description" value={plan.description} onChange={e => update('description', e.target.value)} fullWidth multiline rows={3} /></Grid>
              <Grid item xs={6} sm={3}>
                <TextField select label="Category" value={plan.category} onChange={e => update('category', e.target.value)} fullWidth>
                  {['strength','cardio','flexibility','weight_loss','muscle_gain','general_fitness','yoga','home_training','hiit','mindfulness'].map(c => <MenuItem key={c} value={c}>{c.replace(/_/g,' ')}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField select label="Difficulty" value={plan.difficulty} onChange={e => update('difficulty', e.target.value)} fullWidth>
                  {['beginner','intermediate','advanced'].map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField label="Duration (weeks)" type="number" value={plan.durationWeeks} onChange={e => update('durationWeeks', +e.target.value)} fullWidth inputProps={{ min: 1, max: 52 }} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField label="Days/week" type="number" value={plan.daysPerWeek} onChange={e => update('daysPerWeek', +e.target.value)} fullWidth inputProps={{ min: 1, max: 7 }} />
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" fontWeight={600} mb={1}>Access Type</Typography>
                <Stack direction="row" spacing={2}>
                  <Card
                    variant="outlined"
                    onClick={() => update('isFree', true)}
                    sx={{
                      p: 2, cursor: 'pointer', flex: 1,
                      borderColor: plan.isFree ? 'success.main' : 'divider',
                      borderWidth: plan.isFree ? 2 : 1,
                      bgcolor: plan.isFree ? 'success.50' : 'background.paper',
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                      <LockOpen color={plan.isFree ? 'success' : 'disabled'} />
                      <Typography fontWeight={600}>Free</Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">Available to all users. No subscription required.</Typography>
                  </Card>
                  <Card
                    variant="outlined"
                    onClick={() => update('isFree', false)}
                    sx={{
                      p: 2, cursor: 'pointer', flex: 1,
                      borderColor: !plan.isFree ? 'warning.main' : 'divider',
                      borderWidth: !plan.isFree ? 2 : 1,
                      bgcolor: !plan.isFree ? 'warning.50' : 'background.paper',
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                      <Lock color={!plan.isFree ? 'warning' : 'disabled'} />
                      <Typography fontWeight={600}>Premium Only</Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">Only accessible to users with an active Premium subscription.</Typography>
                  </Card>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <TextField label="Tags (comma separated)" value={(plan.tags || []).join(', ')} onChange={e => update('tags', e.target.value.split(',').map(t => t.trim()).filter(Boolean))} fullWidth helperText="e.g. beginner, strength, home-workout" />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Tab 1: Days & Exercises */}
        {tab === 1 && (
          plan.days.length === 0 ? (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="60vh" width="100%">
              {/* You can replace this with your SVG or illustration */}
              <Box mb={2}>
                <svg width="180" height="80" viewBox="0 0 180 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0 80C0 80 30 0 90 0C150 0 180 80 180 80H0Z" fill="#11182722"/>
                </svg>
              </Box>
              <Typography variant="h6" color="text.secondary" mb={1}>No workout days added</Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>Add days to start building your workout plan.</Typography>
              <Button
                variant="contained"
                startIcon={<Add fontSize="small" />}
                onClick={addDay}
                sx={{ '& .MuiSvgIcon-root': { fontSize: 18, width: 18, height: 18 } }}
              >
                Add First Day
              </Button>
            </Box>
          ) : (
            <Box p={3}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1" fontWeight={600}>Workout Days</Typography>
                <Button
                  size="small"
                  startIcon={<Add fontSize="small" />}
                  onClick={addDay}
                  sx={{ '& .MuiSvgIcon-root': { fontSize: 18, width: 18, height: 18 } }}
                >
                  Add Day
                </Button>
              </Stack>
              {plan.days.map((day, di) => (
                <DayEditor key={di} day={day} dayIndex={di} exercises={filteredEx} exSearch={exSearch}
                  onSearchChange={setExSearch} onUpdateDay={updateDay} onAddExercise={addExercise}
                  onUpdateExercise={updateExercise} onRemoveExercise={removeExercise} onRemoveDay={removeDay}
                  canRemove={plan.days.length > 1}
                />
              ))}
            </Box>
          )
        )}

        {/* Tab 2: Settings */}
        {tab === 2 && (
          <Box p={3}>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <TextField label="Rest Between Sets (sec)" type="number" value={plan.restBetweenSets} onChange={e => update('restBetweenSets', +e.target.value)} fullWidth inputProps={{ min: 0, max: 600 }} />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Rest Between Exercises (sec)" type="number" value={plan.restBetweenExercises} onChange={e => update('restBetweenExercises', +e.target.value)} fullWidth inputProps={{ min: 0, max: 600 }} />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={plan.circuitMode} onChange={e => update('circuitMode', e.target.checked)} />}
                  label={<Box><Typography variant="body2" fontWeight={600}>Circuit Mode</Typography><Typography variant="caption" color="text.secondary">Set1-Ex1 → Set1-Ex2 → Set2-Ex1 (vs linear: Set1-Ex1 → Set2-Ex1 → Set3-Ex1)</Typography></Box>}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={plan.isPublished} onChange={e => update('isPublished', e.target.checked)} />}
                  label={<Box><Typography variant="body2" fontWeight={600}>Published</Typography><Typography variant="caption" color="text.secondary">Published plans are visible to users in the app</Typography></Box>}
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : plan._id ? 'Update Plan' : 'Create Plan'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Day Editor ─────────────────────────────────────────────────────────────────
function DayEditor({ day, dayIndex, exercises, exSearch, onSearchChange, onUpdateDay, onAddExercise, onUpdateExercise, onRemoveExercise, onRemoveDay, canRemove }) {
  const [expanded, setExpanded] = useState(dayIndex === 0);
  const [pickerOpen, setPickerOpen] = useState(false);

  const handleAddExerciseClick = () => {
    setPickerOpen(true);
  };

  const handleUpdateExercise = (dayIdx, exIdx, field, value) => {
    onUpdateExercise(dayIdx, exIdx, field, value);
  };

  const handleRemoveExercise = (dayIdx, exIdx) => {
    onRemoveExercise(dayIdx, exIdx);
  };

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => setExpanded(!expanded)}>
        <DragIndicator sx={{ color: 'text.disabled', mr: 1 }} />
        <Typography variant="subtitle2" fontWeight={600} sx={{ flexGrow: 1 }}>
          Day {day.dayNumber}: {day.dayName}
          {day.isRestDay && <Chip label="Rest" size="small" sx={{ ml: 1 }} />}
          {!day.isRestDay && <Chip label={`${day.exercises.length} exercises`} size="small" color="primary" sx={{ ml: 1 }} />}
        </Typography>
        {canRemove && <IconButton size="small" color="error" onClick={e => { e.stopPropagation(); onRemoveDay(dayIndex); }}><Delete fontSize="small" /></IconButton>}
        {expanded ? <ExpandLess /> : <ExpandMore />}
      </Box>
      <Collapse in={expanded}>
        <Divider />
        <Box p={2}>
          <Grid container spacing={2} mb={2}>
            <Grid item xs={6}><TextField label="Day Name" value={day.dayName} onChange={e => onUpdateDay(dayIndex, 'dayName', e.target.value)} fullWidth size="small" /></Grid>
            <Grid item xs={6}><FormControlLabel control={<Switch size="small" checked={day.isRestDay} onChange={e => onUpdateDay(dayIndex, 'isRestDay', e.target.checked)} />} label="Rest Day" /></Grid>
          </Grid>

          {!day.isRestDay && (
            <>
              {day.exercises.map((ex, ei) => (
                <Box key={ei} sx={{ mb: 1 }}>
                  <Box
                    sx={{
                      display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap',
                      p: 1.5, bgcolor: 'action.hover', borderRadius: 1,
                      position: 'relative',
                    }}
                  >
                  <Typography variant="body2" fontWeight={600} sx={{ minWidth: 140, flex: '1 1 180px' }}>{ex.exerciseName}</Typography>
                  <TextField
                    label="Sets" type="number" value={ex.sets}
                    onChange={e => handleUpdateExercise(dayIndex, ei, 'sets', +e.target.value)}
                    size="small" sx={{ width: 70 }} inputProps={{ min: 1 }}
                  />
                  <TextField
                    label="Reps" type="number" value={ex.reps}
                    onChange={e => handleUpdateExercise(dayIndex, ei, 'reps', +e.target.value)}
                    size="small" sx={{ width: 70 }} inputProps={{ min: 1 }}
                  />
                  <TextField
                    label="Weight (kg)" type="number" value={ex.weight || ''}
                    onChange={e => handleUpdateExercise(dayIndex, ei, 'weight', e.target.value ? +e.target.value : null)}
                    size="small" sx={{ width: 100 }}
                  />
                  {/* Timed toggle */}
                  <Tooltip title={ex.isTimed ? 'Timed (hold/duration)' : 'Rep-based'}>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={!!ex.isTimed}
                          onChange={e => handleUpdateExercise(dayIndex, ei, 'isTimed', e.target.checked)}
                        />
                      }
                      label={<Typography variant="caption">{ex.isTimed ? 'Timed' : 'Reps'}</Typography>}
                      sx={{ mx: 0 }}
                    />
                  </Tooltip>
                  {ex.isTimed && (
                    <TextField
                      label="Secs" type="number" value={ex.durationSeconds || 30}
                      onChange={e => handleUpdateExercise(dayIndex, ei, 'durationSeconds', +e.target.value)}
                      size="small" sx={{ width: 70 }} inputProps={{ min: 5, max: 600 }}
                    />
                  )}
                  <IconButton
                    size="small" color="error"
                    onClick={() => handleRemoveExercise(dayIndex, ei)}
                    aria-label="Remove exercise"
                  >
                    <Close fontSize="small" />
                  </IconButton>
                  </Box>
                {/* Instructions field - shown below each exercise */}
                <TextField
                  label="Instructions / Notes"
                  value={ex.notes || ex.instructions || ''}
                  onChange={e => handleUpdateExercise(dayIndex, ei, 'notes', e.target.value)}
                  size="small" fullWidth multiline maxRows={3}
                  placeholder="How to perform, form cues, modifications..."
                  sx={{ mt: 0.5, mb: 1 }}
                />
                </Box>
              ))}

              <Button
                size="small"
                startIcon={<Add fontSize="small" />}
                onClick={handleAddExerciseClick}
                sx={{
                  mt: 1,
                  minHeight: 32,
                  '& .MuiButton-startIcon': { mr: 0.75, ml: 0 },
                  '& .MuiSvgIcon-root': { fontSize: 18, width: 18, height: 18 },
                }}
              >
                Add Exercise
              </Button>
              <ExercisePickerDialog
                open={pickerOpen}
                exercises={exercises}
                search={exSearch}
                onSearchChange={onSearchChange}
                onClose={() => setPickerOpen(false)}
                onSelect={(ex) => {
                  onAddExercise(dayIndex, ex);
                  setPickerOpen(false);
                }}
              />
            </>
          )}
        </Box>
      </Collapse>
    </Card>
  );
}

function ExercisePickerDialog({ open, exercises, search, onSearchChange, onClose, onSelect }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          '& .MuiSvgIcon-root': {
            width: '1em',
            height: '1em',
            flexShrink: 0,
          },
          '& .MuiIconButton-root .MuiSvgIcon-root': {
            fontSize: 20,
          },
          '& .MuiInputAdornment-root .MuiSvgIcon-root': {
            fontSize: 18,
          },
        },
      }}
    >
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Select Exercise</Typography>
          <IconButton onClick={onClose} aria-label="Close exercise picker"><Close /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 2 }}>
        <TextField
          placeholder="Search exercises..."
          value={search}
          onChange={e => onSearchChange(e.target.value)}
          size="small"
          fullWidth
          autoFocus
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
          sx={{ mb: 2 }}
        />
        <List dense disablePadding sx={{ maxHeight: 360, overflowY: 'auto' }}>
          {exercises.slice(0, 50).map(ex => (
            <ListItemButton key={ex._id || ex.id || ex.name} onClick={() => onSelect(ex)} sx={{ borderRadius: 1 }}>
              <ListItemText
                primary={ex.name}
                secondary={[ex.muscleGroup, ex.activityType].filter(Boolean).join(' - ')}
              />
            </ListItemButton>
          ))}
          {exercises.length === 0 && (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">No exercises found.</Typography>
            </Box>
          )}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}
